import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  IconButton,
  Button,
  Tabs,
  Tab,
  Grid,
  Paper,
  Divider,
  Fade,
  useTheme,
  Stack,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  CameraAlt as Camera,
  Edit,
  Work as CoursesIcon,
  Timeline,
  Group,
  Email,
  PhotoLibrary,
  Forum,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from "framer-motion";
import Header from '../Components/Header';
import StudentSidebar from './StudentSdebar';
import { useAuth } from '../../../utils/AuthContext';

// Sub-components (assuming they will also need MUI migration or stay as is if they are already migrated)
import CoursesSection from '../Components/Profile Section/CourseSection';
import TimelineSection from '../Components/Profile Section/TimelineSection';
import ConnectionsSection from '../Components/Profile Section/ConnectionSection';
import GroupsSection from '../Components/Profile Section/GroupsSection';
import PhotosSection from '../Components/Profile Section/PhotosSection';
import ForumsSection from '../Components/Profile Section/ForumSection';
import EmailInvitesSection from '../Components/Profile Section/EmailInviteSection';

const tabs = [
  { name: 'Profile', icon: <Edit sx={{ fontSize: '1.2rem' }} /> },
  { name: 'Courses', icon: <CoursesIcon sx={{ fontSize: '1.2rem' }} /> },
  { name: 'Timeline', icon: <Timeline sx={{ fontSize: '1.2rem' }} /> },
  { name: 'Connections', icon: <Group sx={{ fontSize: '1.2rem' }} /> },
  { name: 'Groups', icon: <Group sx={{ fontSize: '1.2rem' }} />, count: 1 },
  { name: 'Email Invites', icon: <Email sx={{ fontSize: '1.2rem' }} /> },
];

export default function ProfilePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    profile_pic: '',
    cover_photo: '',
  });
  const [bannerImage, setBannerImage] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const bannerInputRef = useRef(null);
  const profileInputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { userData } = useAuth();

  useEffect(() => {
    if (userData) {
      setProfileData({
        firstName: userData.first_name,
        lastName: userData.last_name,
        nickname: userData.nickname,
        profilePic: userData.profile_pic,
        cover_photo: userData.cover_photo,
      });
      setBannerImage(`data:image/jpeg;base64,${userData.cover_photo}`);
      setProfileImage(userData.profile_pic);
    }
  }, [userData]);

  const handleImageUpload = (event, type) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          if (type === 'banner') setBannerImage(reader.result);
          else setProfileImage(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const currentTabName = tabs[activeTab].name;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Header />
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <StudentSidebar onToggle={setSidebarExpanded} />
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1,
            width: isMobile ? 'calc(100% - 80px)' : (sidebarExpanded ? 'calc(100% - 260px)' : 'calc(100% - 80px)'),
            transition: theme.transitions.create(['margin', 'width'], { duration: 300 }),
            height: 'calc(100vh - 64px)',
            overflowY: 'auto',
          }}
        >
          {/* Cover Photo */}
          <Box
            sx={{
              height: { xs: 160, md: 240 },
              bgcolor: 'action.hover',
              backgroundImage: bannerImage ? `url(${bannerImage})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              cursor: 'pointer',
              '&:hover .cover-edit-overlay': { opacity: 1 },
            }}
            onClick={() => bannerInputRef.current?.click()}
          >
            <input type='file' ref={bannerInputRef} hidden accept='image/*' onChange={(e) => handleImageUpload(e, 'banner')} />
            <Box
              className="cover-edit-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: '0.3s',
              }}
            >
              <Camera sx={{ color: '#fff', fontSize: '2rem' }} />
            </Box>
          </Box>

          <Container sx={{ position: 'relative', mb: 4 }}>
            {/* Profile Avatar and Basic Info */}
            <Box sx={{ mt: { xs: -6, md: -10 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-end' }, gap: 3, px: 2 }}>
              <Box 
                sx={{ 
                  position: 'relative',
                  '&:hover .avatar-edit-overlay': { opacity: 1 }
                }}
              >
                <Avatar
                  src={profileImage}
                  sx={{
                    width: { xs: 120, md: 180 },
                    height: { xs: 120, md: 180 },
                    border: `6px solid ${theme.palette.background.paper}`,
                    boxShadow: theme.shadows[4],
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                  }}
                  onClick={() => profileInputRef.current?.click()}
                >
                  <Camera sx={{ fontSize: '2rem' }} />
                </Avatar>
                <input type='file' ref={profileInputRef} hidden accept='image/*' onChange={(e) => handleImageUpload(e, 'profile')} />
                <Box
                  className="avatar-edit-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    borderRadius: '50%',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: '0.3s',
                    color: '#fff',
                    pointerEvents: 'none',
                  }}
                >
                  <Camera fontSize="small" />
                  <Typography variant="caption" sx={{ mt: 0.5 }}>CHANGE</Typography>
                </Box>
              </Box>

              <Box sx={{ flexGrow: 1, textAlign: { xs: 'center', md: 'left' }, pb: 1 }}>
                <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.5px' }}>
                  {profileData.firstName} {profileData.lastName}
                </Typography>
                <Typography variant="body1" color="text.secondary" fontWeight={500}>
                  @{profileData.nickname} • Joined May 2023 • <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>Active now</Box>
                </Typography>
              </Box>

              <Box sx={{ pb: 1 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<Edit />}
                  onClick={() => navigate('/db-shiksha/kaas/student/editProfile')}
                  sx={{ borderRadius: '50px', fontWeight: 700, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                >
                  Edit Profile
                </Button>
              </Box>
            </Box>

            {/* Navigation Tabs */}
            <Paper 
              elevation={0} 
              sx={{ 
                mt: 4, 
                borderRadius: '16px', 
                border: '1px solid', 
                borderColor: 'divider',
                overflow: 'hidden'
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTabs-indicator': { height: 4, borderRadius: '4px 4px 0 0', bgcolor: '#D4AF37' },
                  '& .MuiTab-root': { py: 2.5, px: 3, fontWeight: 700, minHeight: 64 }
                }}
              >
                {tabs.map((tab, idx) => (
                  <Tab
                    key={tab.name}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {tab.icon}
                        {tab.name}
                        {tab.count && (
                          <Box sx={{ bgcolor: activeTab === idx ? '#D4AF37' : 'action.selected', color: activeTab === idx ? '#fff' : 'text.secondary', px: 1, borderRadius: '10px', fontSize: '0.75rem' }}>
                            {tab.count}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Paper>

            {/* Content Section */}
            <Box sx={{ mt: 3, minHeight: 400 }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {currentTabName === 'Profile' && (
                    <Paper sx={{ p: 4, borderRadius: '20px', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="h6" fontWeight={800} gutterBottom sx={{ mb: 3 }}>General Information</Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} uppercase>FIRST NAME</Typography>
                          <Typography variant="body1" fontWeight={600}>{profileData.firstName || 'Not provided'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} uppercase>LAST NAME</Typography>
                          <Typography variant="body1" fontWeight={600}>{profileData.lastName || 'Not provided'}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} uppercase>NICKNAME</Typography>
                          <Typography variant="body1" fontWeight={600}>{profileData.nickname || 'Not provided'}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                  {currentTabName === 'Timeline' && <TimelineSection />}
                  {currentTabName === 'Courses' && <CoursesSection />}
                  {currentTabName === 'Connections' && <ConnectionsSection />}
                  {currentTabName === 'Groups' && <GroupsSection />}
                  {currentTabName === 'Email Invites' && <EmailInvitesSection />}
                </motion.div>
              </AnimatePresence>
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
